import { getJson, getList, send } from '../api/client';
import type { HelpPageSnapshot } from './pageContext';
import type { HelpMessage, HelpThread } from './threads';

/** One source card under an assistant answer — resolved server-side from the
 * articles the model actually read, never parsed out of prose. */
export interface HelpSource {
  id: string;
  title: string;
  product: string;
  module: string;
  handbookPath: string;
  deepLink: string | null;
  navPath: string | null;
  imageUrl: string | null;
}

/** How to reach a human. Static per deployment, so it rides /help/status once. */
export interface HelpSupport {
  email: string;
  phone: string;
  /** Digits only — `tel:` dials nothing if the pretty form is used. */
  phoneHref: string;
  /** False without a platform mail account: the button would only ever fail. */
  canRaiseTicket: boolean;
  /**
   * Whether "Talk to a person" is offered — false until the deployment has a
   * way for a person to answer. Absent from a server that predates it.
   */
  canHandoff?: boolean;
  /** Whether the support desk is inside its hours (set from sphere-watch). */
  onCall?: 'staffed' | 'after_hours';
  /** When a closed desk next opens — an instant, shown in the operator's own time. */
  nextOpenAt?: string | null;
}

/**
 * A request for a LimoSphere person on this conversation. Never names who —
 * the operator talks to one "LimoSphere Support" voice.
 */
export interface HelpHandoff {
  id: string;
  status: 'waiting' | 'with_staff';
  urgency: 'normal' | 'urgent';
  staffName: string | null;
  waitingSince: string | null;
  claimedAt: string | null;
  expiresAt: string | null;
}

export interface HelpAnswer {
  thread: HelpThread;
  /** Null while a person holds the thread: nobody answered, and the poll brings their reply. */
  message: HelpMessage | null;
  /**
   * The operator's own turn as the server stored it. The panel drew that turn
   * optimistically from local file handles; this is the copy with real ids and
   * presigned urls to swap in.
   */
  question: HelpMessage;
  answer: string | null;
  sources: HelpSource[];
  /** The model called the escalate tool — never inferred from its wording. */
  unresolved: boolean;
  /** Present when the turn went to a person rather than the assistant. */
  handoff?: HelpHandoff | null;
  usage: { inputTokens: number | null; outputTokens: number | null };
}

export interface HelpStatus {
  available: boolean;
  reason?: string;
  provider?: string;
  model?: string;
  /** False without an S3 bucket: the clip would only ever fail on click. */
  canAttach?: boolean;
  support?: HelpSupport;
}

/** Screenshots only — see HELP_IMAGE_TYPES in controller/helpCont.js. */
export const HELP_UPLOAD_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
export const HELP_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
export const HELP_MAX_ATTACHMENTS = 3;

export const getHelpStatus = () => getJson<HelpStatus>('/help/status');

/** Opening questions for a screen — the handbook's own authored phrases. */
export const getHelpSuggestions = (route: string) =>
  getJson<{ screen: string | null; questions: string[] }>(
    `/help/suggestions?route=${encodeURIComponent(route)}`,
  );

/**
 * One message and (optionally) the thread it belongs to — the server holds the
 * transcript and decides how much of it the model sees.
 */
export const sendHelpChat = (
  message: string,
  threadId: string | null,
  context?: { route?: string; page?: HelpPageSnapshot },
  attachments?: { key: string }[],
) =>
  send<HelpAnswer>('POST', '/help/chat', {
    message,
    ...(threadId ? { threadId } : {}),
    ...(context ? { context } : {}),
    ...(attachments?.length ? { attachments } : {}),
  });

/**
 * A screenshot goes to S3 first and the question names its key — a presigned
 * PUT, like the agent composer's, because this caller is signed in. The key is
 * minted by the server, so /help/chat can tell an attachment of ours from a
 * string somebody typed.
 */
export const presignHelpUpload = (fileName: string, contentType: string) =>
  send<{ s3Key: string; fileName: string; putUrl: string }>('POST', '/help/uploads/presign', {
    fileName,
    contentType,
  });

export const listHelpThreads = () => getList<HelpThread>('/help/threads');

export const getHelpThread = (id: string) =>
  getJson<
    HelpThread & {
      messages: HelpMessage[];
      /** The open request for a person, so a reload picks the conversation back up. */
      handoff?: HelpHandoff | null;
      /** The last message id — where a tail poll starts. */
      cursor?: string;
    }
  >(`/help/threads/${id}`);

/**
 * What has been written since `after`: a person's replies and the desk's
 * notices. Polled only while a request is open; `nextPollMs` is the server's
 * pace, and 0 means the person has gone and the poll should stop.
 */
export const pollHelpThread = (id: string, after: string) =>
  getJson<{ messages: HelpMessage[]; handoff: HelpHandoff | null; nextPollMs: number }>(
    `/help/threads/${id}/messages?after=${encodeURIComponent(after)}`,
  );

/** "Talk to a person". Idempotent — a second press returns the same request. */
export const requestHelpHandoff = (
  threadId: string,
  body: { urgency: 'normal' | 'urgent'; context?: { route?: string; page?: HelpPageSnapshot } },
) =>
  send<{ handoff: HelpHandoff; message: HelpMessage | null; support: HelpSupport }>(
    'POST',
    `/help/threads/${threadId}/handoff`,
    body,
  );

/** "I'm fine now" — the assistant answers the next message again. */
export const cancelHelpHandoff = (threadId: string) =>
  send<{ handoff: null; message: HelpMessage | null }>('POST', `/help/threads/${threadId}/handoff/cancel`, {});

export const deleteHelpThread = (id: string) =>
  send<{ deleted: boolean }>('DELETE', `/help/threads/${id}`);

/**
 * Mail LimoSphere support an AI-drafted summary of this conversation, and the
 * operator a receipt. `acknowledgedTo` is null when dispatch holds no email for
 * them — the panel must not promise a receipt that will never arrive.
 */
export const raiseHelpTicket = (threadId: string) =>
  send<{
    ticket: { id: string; subject: string; status: string; createdAt: string };
    acknowledgedTo: string | null;
    support: HelpSupport;
  }>('POST', `/help/threads/${threadId}/ticket`, {});
